import { Icons } from '@assets/svg'
import { colors } from '@config/theme'
import Eventbus from '@libs/eventbus'
import { Camp } from '@libs/types/Camp.ts'
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material'
import { setCurrentPage } from '@store/campSearch/campSearchSlice'
import { AddToListDrawer } from '@stories/Components/AddtoList/AddToListDrawer.tsx'
import mixpanel from 'mixpanel-browser'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'

import boostedValues from '../../camp_boost_order.json'
import CampListSortButton from '../../src/components/CampList/CampListSortButton.tsx'
import { CampListItemSkeleton } from '../components/CampList/CampListItem.skeleton.tsx'
import { CampListItem } from '../components/CampList/CampListItem.tsx'
import CampListSearchBar from '../components/CampList/CampListSearchBar.tsx'
import Paginate from '../components/Paginate/index.tsx'
import { useAppDispatch, useAppSelector } from '../store/hooks.ts'
import MapComponent from '../stories/Components/MapView/MapViewComponent.tsx'

interface NormalizedMarker {
  id: number
  address: string
  lat: number
  lng: number
  dirty: boolean
  camps: Camp[]
}

const tabs = [
  { index: 0, label: 'Detail view', id: 'detail_view', icon: <Icons.DetailViewIcon /> },
  { index: 1, label: 'Provider Map View', id: 'provider-map-view', icon: <Icons.ProviderMapViewIcon /> },
]

const CampList: React.FC = () => {
  const dispatch = useAppDispatch()

  const { campListFiltered, isLoading, isFilteringLoading } = useAppSelector((state) => state.campSearch)
  const selectedMetro = useAppSelector((state) => state.metro.selectedMetro)

  const pagination = useAppSelector((state) => state.campSearch.pagination)
  const sortBy = useAppSelector((state) => state.campFilters.sort.by)

  const [favCamId, setFavCampId] = useState<number>(0)

  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page))
  }

  const [activeTab, setActiveTab] = React.useState(0)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  useEffect(() => {
    mixpanel.track_pageview({ page: 'Camps' })
    Eventbus.on(Eventbus.RESET_PAGING, () => {
      dispatch(setCurrentPage(1))
    })
  }, [])

  // Step 1: Sort the boosts array
  const sortBoosts = boostedValues.sort((a, b) => b.boost_value - a.boost_value)

  // Step 2: Merge top boosts with allCamps
  const boostedCamps = sortBoosts
    .map((boost) => {
      const camp = campListFiltered.find((camp) => camp.id === boost.camp_id)
      return camp ? camp : null // Merge only if camp exists
    })
    .filter((camp) => camp !== null)
    .slice(0, 4)

  // Step 3: Check if there are less than 4 boosted camps and add additional Camperoni boosted camps
  let additionalCamperoniBoostedCamps: Camp[] = []
  if (boostedCamps.length > 0 && boostedCamps.length < 4) {
    const remainingSlots = 4 - boostedCamps.length
    additionalCamperoniBoostedCamps = campListFiltered.slice(-remainingSlots)
  }

  function normalizeCamps(camps: Camp[]): NormalizedMarker[] {
    const markersMap = new Map<string, NormalizedMarker>()

    camps.forEach((camp) => {
      // Skip if camp address id is not available or lat or lng is 0 or dirty is true.
      if (!camp.address_id || !camp.lat || !camp.lng || camp.address_dirty) {
        return
      }
      // Use address_id as the key if available, otherwise fallback to address text.
      const key = camp.address_id ? camp.address_id.toString() : camp.address

      // Convert lat/lng strings to numbers.
      const latNum = camp.lat
      const lngNum = camp.lng

      // Initialize marker groups if it doesn't exist.
      if (!markersMap.has(key)) {
        markersMap.set(key, {
          id: camp.address_id,
          address: camp.address,
          lat: latNum,
          lng: lngNum,
          dirty: camp.address_dirty,
          camps: [camp],
        })
      } else {
        // Add to camps array.
        const existingMarker = markersMap.get(key)!
        existingMarker.camps.push(camp)
      }
    })

    // Convert the Map to an array.
    return Array.from(markersMap.values())
  }

  return (
    <>
      <Helmet>
        <title>Camperoni Website - Camp search</title>
        <link rel="canonical" href={`https://www.camperoni.com/camps`} />
      </Helmet>
      <Box sx={{ flexGrow: 1 }} px={2}>
        <Box pb={3}>
          <Typography component="h1" variant="h4" data-testid="search_heading">
            {`${selectedMetro ? selectedMetro.name : ''} Camp Search`}
          </Typography>
        </Box>
        <CampListSearchBar />
        <Box
          mb={2}
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column-reverse', // small and below
              md: 'row', // medium and up
            },
            alignItems: {
              xs: 'flex-start',
              md: 'flex-start',
            },
            justifyContent: {
              xs: 'flex-start',
              md: 'space-between',
            },
            gap: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { backgroundColor: colors.brandBlue } }}
            sx={{
              height: '40px',
              minHeight: '40px',
              '& .MuiTabs-flexContainer': {
                height: '40px',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: colors.brandBlue,
                height: '2px',
              },
              '& .MuiTab-root': {
                height: '40px',
              },
            }}
          >
            {tabs.map((tab, index: number) => (
              <Tab
                disableRipple={true}
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
                data-testid={`${tab.id}-button`}
                sx={{
                  textTransform: 'none',
                  marginRight: 2,
                  height: '40px',

                  padding: '0 4px ',
                  fontWeight: 600,
                  fontSize: '16px',
                  color: colors.brandGray500,
                  gap: 1,
                  '&.MuiTab-root': {
                    minHeight: '36px',
                  },
                  '&.Mui-selected': {
                    color: colors.brandBlue,
                    '& .MuiTab-iconWrapper': {
                      color: colors.brandBlue,
                    },
                  },
                  '& .MuiTab-iconWrapper': {
                    color: colors.brandGray500,
                    marginRight: 0,
                  },
                }}
              />
            ))}
          </Tabs>
          <CampListSortButton />
        </Box>
        {activeTab === 0 && (
          <Box>
            {isLoading || isFilteringLoading ? (
              <Grid container columnSpacing={{ xs: 1, sm: 4 }} rowSpacing={{ xs: 2, sm: 4 }}>
                {Array.from(new Array(12)).map((_, index) => (
                  <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
                    <CampListItemSkeleton />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <>
                {campListFiltered.length >= 15 &&
                  boostedCamps.length > 0 &&
                  pagination.currentPage === 1 &&
                  sortBy === 'featured' && (
                    <Grid container columnSpacing={{ xs: 1, sm: 4 }}>
                      {[...boostedCamps, ...additionalCamperoniBoostedCamps].map((camp, index: number) => (
                        <CampListItem
                          key={index}
                          item={camp}
                          setFavCampId={setFavCampId}
                          isBoosted={true}
                          campIndex={index + 1}
                          campPagePosition={index + 1}
                          pageIndex={pagination.currentPage}
                        />
                      ))}
                    </Grid>
                  )}

                {campListFiltered && campListFiltered.length > 0 ? (
                  <Grid container columnSpacing={{ xs: 1, sm: 4 }} data-testid="camp-items-container">
                    <Paginate
                      itemsPerPage={20}
                      data={campListFiltered}
                      renderItem={(item: Camp, index: number) => (
                        <CampListItem
                          item={item}
                          setFavCampId={setFavCampId}
                          campIndex={
                            (pagination.currentPage - 1) * 20 +
                            index +
                            (campListFiltered.length >= 15 && boostedCamps.length > 0 && sortBy === 'featured' ? 5 : 1)
                          }
                          campPagePosition={
                            index +
                            (campListFiltered.length >= 15 &&
                            boostedCamps.length > 0 &&
                            sortBy === 'featured' &&
                            pagination.currentPage === 1
                              ? 5
                              : 1)
                          }
                          pageIndex={pagination.currentPage}
                        />
                      )}
                      currentPage={pagination.currentPage}
                      onPageChange={handlePageChange}
                    />
                  </Grid>
                ) : (
                  <Typography variant="h6">No camps found. Try clearing your filters.</Typography>
                )}
              </>
            )}
          </Box>
        )}
        {activeTab === 1 && <MapComponent camps={normalizeCamps(campListFiltered)} />}
      </Box>
      <AddToListDrawer campId={favCamId} onClose={() => setFavCampId(0)} />
    </>
  )
}

export default CampList
